import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  AppLayout,
  Box,
  Button,
  Container,
  ContentLayout,
  Grid,
  Icon,
  Input,
  KeyValuePairs,
  Link,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react/polaris";

import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";

import SideNav from "../../Home/SideNav";
import ControlPanelModal from "./ControlPanelModal";
import AddColumnModal from "./AddColumnModal";
import RowSettingsModal from "./RowSettingsModal";
import RoleAliasCellEditor from "./RoleAliasCellEditor";
import CreateTaskManagerModal from "./CreateTaskManagerModal";
import AdhocTasks from "./AdhocTasks";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from "../../utilities/adminUtils";
import { captureMetrics } from "src/katalMetrics/metricsHelperMethods";
import { ACTION_METHODS, METRIC_TYPE, SITE_SERVICES } from "src/katalMetrics/metricsModels";
import { getTaskManagerAuditRoute, TASK_MANAGER_ROUTE } from "./constants";
import {
  getDashboard,
  getServices,
  getLogoViewUrl,
  fetchAllApps,
  getTaskManagerColumnConfig,
  saveTaskManagerColumnConfig,
  getTaskManagerSettings,
  getTaskManagerStatusMappings,
  saveTaskManagerSettings,
  updateProcessLiteViaTaskManager,
  updateFlexicoTemplateViaTaskManager,
  updateLogicoProcessLiteViaTaskManager,
  manageServiceAttributes,
  manageTrackingUpdates,
} from "./api";
import { getCachedLdaps } from "src/checkPageAccess";
import { createAuditLog } from "../Templates/Utils/AppActivityUtils";
// Reuse the CorpReporting deliverables styling (small_values, info_message tags, etc.)
// so this page matches the look-and-feel of the CRA deliverables dashboard.
import "../CorpReporting/styles.scss";

const DEFAULT_TAB_ID = "default";
const asList = (v) => (Array.isArray(v) ? v : []);

// The dashboard logo (or a default Cloudscape icon tile when no logo is set).
const DashboardLogo = ({ name, logoUrl }) =>
  logoUrl ? (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 8, flexShrink: 0 }}
    />
  ) : (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 8,
        background: "#ec7211",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon name="multiscreen" variant="inverted" size="medium" />
    </div>
  );

// A single metric value styled as a KPI number, reusing Cloudscape Box
// typography so it inherits the portal font.
const Metric = ({ value, danger }) => (
  <Box variant="h1" color={danger ? "text-status-error" : "text-status-info"} padding="n">
    {value}
  </Box>
);

// Top summary section built entirely from Cloudscape primitives: a Grid splits
// the row into the identity Container and the metrics Container, and
// KeyValuePairs renders the deliverable counts as a labelled KPI strip.
const SummaryBar = ({
  name,
  logoUrl,
  allDeliverables,
  notDue,
  dueToday,
  overdue,
  canManage,
  canEdit,
  onToggleAdmin,
  onEdit,
}) => (
  <Grid
    gridDefinition={[
      { colspan: { default: 12, s: 3 } },
      { colspan: { default: 12, s: 9 } },
    ]}
  >
    <Container>
      <SpaceBetween direction="horizontal" size="s" alignItems="center">
        <DashboardLogo name={name} logoUrl={logoUrl} />
        <SpaceBetween size="xxs">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box variant="h2" padding="n">
              {name}
            </Box>
            {canEdit && (
              <Button
                variant="inline-icon"
                iconName="edit"
                ariaLabel="Edit dashboard"
                onClick={onEdit}
              />
            )}
          </SpaceBetween>
          {canManage && (
            <Button variant="inline-link" iconName="settings" onClick={onToggleAdmin}>
              Admin controls
            </Button>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>

    <Container>
      <KeyValuePairs
        columns={4}
        items={[
          { label: "All Deliverables", value: <Metric value={allDeliverables} /> },
          { label: "Not Due", value: <Metric value={notDue} /> },
          { label: "Due Today", value: <Metric value={dueToday} /> },
          { label: "Overdue", value: <Metric value={overdue} danger /> },
        ]}
      />
    </Container>
  </Grid>
);

// preparer = uploaders. Keep the typed shape ({ alias, type }) so the editor
// can offer USER (UPLOADER) vs LDAP (UPLOADER_LDAP).
const preparersOf = (svc) =>
  asList(svc.uploaders)
    .filter((u) => u && u.alias)
    .map((u) => ({ alias: u.alias, type: u.type || "UPLOADER" }));

// approvers list carries column_index: 0 = reviewer, 1 = approver. Keep the
// typed shape ({ alias, type, column_index }) for the editor + save.
const approverRoleOf = (svc, columnIndex) =>
  asList(svc.approvers)
    .filter((a) => a && a.alias && Number(a.column_index) === columnIndex)
    .map((a) => ({ alias: a.alias, type: a.type || "APPROVER", column_index: columnIndex }));

// Bare aliases from a typed role array (for search text / membership checks).
const aliasesOf = (rows) => asList(rows).map((r) => r && r.alias).filter(Boolean);

// Quick-filter text for a role column: the aliases joined, so the search box
// still matches even though the cell value is an array of objects.
const roleQuickFilterText = (params) => aliasesOf(params.value).join(" ");

// Display renderer for a role column: groups the typed alias rows into a USER
// line and an LDAP line (mirrors DeliverablesPage renderAliases).
const RoleCell = (params) => {
  const rows = asList(params.value);
  const users = rows
    .filter((r) => r && r.type && !String(r.type).endsWith("_LDAP"))
    .map((r) => r.alias)
    .filter(Boolean);
  const ldaps = rows
    .filter((r) => r && String(r.type).endsWith("_LDAP"))
    .map((r) => r.alias)
    .filter(Boolean);
  return (
    <Box>
      {users.length > 0 && (
        <div>
          <span className="small_values">{users.join(", ")}</span>
        </div>
      )}
      {ldaps.length > 0 && (
        <div>
          <span className="small_values">{ldaps.join(", ")}</span>
        </div>
      )}
    </Box>
  );
};

// ---- Deliverable tag helpers (mirror CorpReporting/DeliverablesPage) ----

// The DISABLED custom attribute shows the grey "Disabled" badge.
const isDisabledOf = (svc) =>
  (asList(svc.custom_attributes).find((a) => a && a.attribute_type === "DISABLED")?.attribute || "false") === "true";

// Sub-group key the listener writes when a service has no sub-group column
// configured. Keep in sync with TransactionTracker.DEFAULT_SUB_GROUP.
const DEFAULT_TRACKING_SUB_GROUP = "UNKNOWN";

// Font colour per status option in the Mapping Configuration tab.
const STATUS_FONT_COLORS = {
  SUCCESS: "green",
  ERROR: "red",
  WARNING: "orange",
};

// Index the dashboard's status mappings by source status for O(1), case-insensitive
// lookup: raw tracking status -> { display status, font colour }.
const indexStatusMappings = (mappings) =>
  asList(mappings).reduce((index, mapping) => {
    const sourceStatus = String(mapping?.source_status || "").trim().toLowerCase();
    if (!sourceStatus) return index;
    index[sourceStatus] = {
      displayStatus: String(mapping?.display_status || "").trim(),
      color: STATUS_FONT_COLORS[String(mapping?.color || "").trim().toUpperCase()] || null,
    };
    return index;
  }, {});

// tracking_status arrives from getServices as { tracking_sub_group: status }.
const trackingStatusOf = (svc) => {
  const tracking = svc?.tracking_status;
  if (!tracking || typeof tracking !== "object" || Array.isArray(tracking)) return {};
  return Object.entries(tracking).reduce((statuses, [subGroup, status]) => {
    const key = String(subGroup || "").trim();
    if (key) statuses[key] = status ?? "";
    return statuses;
  }, {});
};

// Case-insensitive lookup so a DATE column's date type matches the stored
// tracking_sub_group regardless of how the admin typed it.
const lookupBySubGroup = (map, subGroup) => {
  if (!map) return "";
  const wanted = String(subGroup || "").trim().toLowerCase();
  if (!wanted) return "";
  const match = Object.keys(map).find((key) => key.toLowerCase() === wanted);
  return match ? map[match] ?? "" : "";
};

const trackingStatusFor = (row, subGroup) => lookupBySubGroup(row?.tracking_status, subGroup);


// A tracking-status cell derives its displayed value from tracking_status via a
// valueGetter. If the editor wrote the picked value anywhere else, the getter would
// keep returning the old value, AG Grid would see newValue === oldValue and skip
// onCellValueChanged, and no save would fire. So the editor stashes the picked value
// in a per-sub-group pending map that the getter prefers; the value then really
// changes, the change event fires, and the save handler reads event.newValue.
const pendingStatusOf = (row, subGroup) => {
  const pending = row?._pendingStatus;
  const key = String(subGroup || "").trim().toLowerCase();
  if (!pending || !key || !(key in pending)) return undefined;
  return pending[key];
};

const setPendingStatus = (row, subGroup, value) => {
  const key = String(subGroup || "").trim().toLowerCase();
  if (!key) return false;
  row._pendingStatus = { ...(row._pendingStatus || {}), [key]: value };
  return true;
};

const clearPendingStatus = (row, subGroup) => {
  const key = String(subGroup || "").trim().toLowerCase();
  if (!row?._pendingStatus || !key) return;
  const next = { ...row._pendingStatus };
  delete next[key];
  row._pendingStatus = next;
};

const trackingFilesOf = (svc) => {
  const files = svc?.tracking_files;
  if (!files || typeof files !== "object" || Array.isArray(files)) return {};
  return Object.entries(files).reduce((accumulated, [subGroup, fileId]) => {
    const key = String(subGroup || "").trim();
    if (key) accumulated[key] = fileId ?? "";
    return accumulated;
  }, {});
};

/**
 * Link target for a tracked status.
 *
 * FRISCO and FlexiCo both have a per-transaction details page keyed by the
 * transaction's file id, which the listener stored alongside the status. LOGICO
 * has no file details route, so it goes to the process history page instead.
 */
const transactionHrefOf = (row, subGroup) => {
  const source = String(row?.source || "").trim().toUpperCase();
  const appId = idOrNull(row?.appId);

  if (source === "LOGICO") {
    const logicoProcessId = row?.logicoProcessId;
    if (!appId || !logicoProcessId) return null;
    return `/App/${appId}/LogicoProcess/${logicoProcessId}/history`;
  }

  const fileId = String(lookupBySubGroup(row?.tracking_files, subGroup) || "").trim();
  if (!fileId) return null;
  if (source === "FRISCO") return `/frisco/file/${encodeURIComponent(fileId)}`;
  if (source === "FLEXICO") return `/flexico/file/${encodeURIComponent(fileId)}`;
  return null;
};

const taskManagerAttributesOf = (svc) => {
  if (svc?.task_manager_attributes && !Array.isArray(svc.task_manager_attributes)) {
    return { ...svc.task_manager_attributes };
  }
  return asList(svc?.custom_attributes).reduce((attributes, item) => {
    const key = item?.attribute_type;
    if (key) attributes[key] = item?.attribute_value ?? item?.attribute ?? "";
    return attributes;
  }, {});
};

// Build the { columnName: value } map the grid renders, choosing the source per
// column: a track_by_cadence column reads the current period's value from
// service.tracking_custom_attributes; a plain column reads service.service_attributes.
// getServices returns those two maps separately (keyed by column name) so the
// UI - which owns the column config - decides which one each column shows. A
// cadence column with no value for the current period intentionally renders blank.
const resolveCustomColumnValues = (svc, columns) => {
  const serviceAttributes =
    svc?.service_attributes && !Array.isArray(svc.service_attributes)
      ? svc.service_attributes
      : {};
  const trackingAttributes =
    svc?.tracking_custom_attributes && !Array.isArray(svc.tracking_custom_attributes)
      ? svc.tracking_custom_attributes
      : {};

  // Fallback for any pre-existing base values getServices may still send.
  const base = taskManagerAttributesOf(svc);
  const resolved = { ...base };

  asList(columns)
    .filter((column) => column?.type && column.type !== "DEFAULT" && column?.name)
    .forEach((column) => {
      const source = column.track_by_cadence ? trackingAttributes : serviceAttributes;
      // Only override when the chosen source actually carries the column, so we
      // don't blank out a value that arrived via the base map. Missing in the
      // chosen source -> blank, which is the correct period-scoped behavior for
      // cadence columns.
      if (Object.prototype.hasOwnProperty.call(source, column.name)) {
        resolved[column.name] = source[column.name];
      } else {
        resolved[column.name] = "";
      }
    });

  return resolved;
};

// Treat null/undefined/blank ids as "missing" so we never build a broken link
// like /App/1/flexico//upload. Returns the trimmed id string, or null.
const idOrNull = (val) => {
  if (val === null || val === undefined) return null;
  const str = String(val).trim();
  return str === "" ? null : str;
};

// The FRISCO process id for this service (null when absent, e.g. LOGICO rows).
const processIdOf = (svc) => idOrNull(svc?.process_id ?? svc?.processId ?? svc?.pid);

// The Flexico flex/template id for this service (null when absent).
const flexIdOf = (svc) => idOrNull(svc?.flex_id ?? svc?.flexId ?? svc?.fid);

// The Logico process id for this service (null when absent).
const logicoProcessIdOf = (svc) =>
  idOrNull(svc?.logico_process_id ?? svc?.logicoProcessId ?? svc?.lpid);

const serviceRowKey = (row) => {
  const source = String(row?.source || "").toUpperCase();
  const serviceId = row?.processId ?? row?.flexId ?? row?.logicoProcessId ?? "unknown";
  return `${source}:${row?.appId ?? "unknown"}:${serviceId}`;
};

/**
 * Build the destination for a deliverable, or null if we can't route it.
 * Mirrors CorpReporting/DeliverablesPage: a service is Flexico when it carries
 * a `flex_id` (-> flexico execution/upload page), Logico when it carries a
 * `logico_process_id` (-> logico upload page), otherwise FRISCO via its
 * `process_id` (-> process upload page). All need the owning `app_id`.
 *
 * Any service missing the required ids (a row with no id) simply returns null
 * and renders as plain, non-clickable text — never a crash.
 */
const deliverableHrefOf = (svc) => {
  const appId = idOrNull(svc?.app_id ?? svc?.appId);
  if (!appId) return null;
  const flexId = flexIdOf(svc);
  if (flexId) {
    return `/App/${appId}/flexico/${flexId}/upload`;
  }
  const logicoProcessId = logicoProcessIdOf(svc);
  if (logicoProcessId) {
    return `/App/${appId}/LogicoProcess/${logicoProcessId}/upload`;
  }
  const processId = processIdOf(svc);
  if (processId) {
    return `/App/${appId}/process/${processId}/upload`;
  }
  return null;
};

/**
 * Task Manager dashboard detail page.
 *
 * Unique per-dashboard URL: /taskmanager/:dashboardId (e.g. /taskmanager/3).
 * Renders the services (FRISCO / FLEXICO / LOGICO) associated with the
 * dashboard's apps in an ag-grid table. getServices returns each service with
 * raw uploaders + approvers lists; the UI derives Preparer (uploaders),
 * Approver (approvers column_index 1) and Reviewer (approvers column_index 2).
 *
 * The API returns at most 100 rows per call plus `hasMore` / `nextOffset`;
 * "Load more" pulls the next page and appends.
 */
const TaskManagerDashboard = () => {
  const { dashboardId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const gridRef = useRef(null);
  // Guards against React 18 StrictMode's double effect invoke (dev), which
  // would otherwise fire getServices twice for the same dashboard.
  const fetchedDashboardId = useRef(null);

  // Seed from the dashboard passed via router state (from the list page) so the
  // title shows the name right away, even before the data load resolves.
  const [dashboard, setDashboard] = useState(location.state?.dashboard || null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [nextOffset, setNextOffset] = useState(null);
  // Server-reported total across all pages, so the "All Deliverables" KPI is
  // accurate before every page is loaded (rowData only holds fetched pages).
  const [totalCount, setTotalCount] = useState(null);
  const [navOpen, setNavOpen] = useState(true);
  const [quickFilter, setQuickFilter] = useState("");

  // Gear (settings) + "Add column" are admin-only actions on the dashboard header.
  const isPortalAdmin = useSelector(selectIsEffectiveAdmin);
  const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin);
  // Editing the dashboard itself (name/apps/logo) is restricted to portal and
  // support admins only.
  const canEditDashboard = isPortalAdmin || isSupportAdmin;
  // Current user + their LDAP groups, used for dashboard ownership and to scope
  // non-admin rows to deliverables they prepare / review / approve.
  const userId = useSelector((s) => s.auth.userId);
  const userLDAPS = useSelector((s) => s.auth.userLDAPS);
  const currentStage = useSelector((s) => s.auth.currentStage);
  // Resolved LDAP group memberships (same source DeliverablesPage uses for
  // "my deliverables"), so LDAP-typed roles the user belongs to also match.
  const [ldapGroups, setLdapGroups] = useState([]);
  const normalizedUserId = String(userId || "").toLowerCase();
  const ownerLdapGroups = new Set(
    [...asList(userLDAPS), ...asList(ldapGroups)]
      .filter(Boolean)
      .map((group) => String(group).toLowerCase())
  );
  const isDashboardOwner = asList(dashboard?.owners).some((owner) => {
    const alias = String(owner?.alias || "").toLowerCase();
    if (!alias) return false;
    return String(owner?.type || "").endsWith("_LDAP")
      ? ownerLdapGroups.has(alias)
      : alias === normalizedUserId;
  });
  // Grid management is available to portal/support admins and configured
  // dashboard owners. Viewers retain read-only access.
  const canManage = isPortalAdmin || isSupportAdmin || isDashboardOwner;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsPreferenceConfig, setSettingsPreferenceConfig] = useState(null);
  const [settingsDashboard, setSettingsDashboard] = useState(null);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  // Edit-dashboard dialog (name / apps / logo), reusing the listing-page modal.
  const [editOpen, setEditOpen] = useState(false);
  // Toggles the dashboard-level "AdHoc tasks" panel (create / list / confirm).
  const [adhocOpen, setAdhocOpen] = useState(false);
  // Per-row "Row Settings" dialog opened from the admin gear.
  const [rowSettingsRow, setRowSettingsRow] = useState(null);
  // Rows currently checked in the grid; drives the bulk admin actions.
  const [selectedRows, setSelectedRows] = useState([]);
  // Service rows with an alias update currently in flight are locked until the
  // source API finishes, preventing overlapping edits.
  const [savingRoleRows, setSavingRoleRows] = useState(() => new Set());
  // Toggles the small admin-controls panel shown to the right of the grid.
  const [adminControlsOpen, setAdminControlsOpen] = useState(false);
  // "Save view" is enabled only when the current columns differ from the
  // saved/default baseline.
  const [viewDirty, setViewDirty] = useState(false);
  const baselineColStateRef = useRef(null);
  // app_id -> app name, for the "App" grid column.
  const [appsById, setAppsById] = useState({});
  // Control Panel "Status Mapping Configuration" rows: the raw transaction status
  // (key) mapped to the text to display (value) and the colour to show (status).
  const [statusMappings, setStatusMappings] = useState([]);
  // Per-dashboard/tab custom columns persisted through GenericPreference.
  const [customColumns, setCustomColumns] = useState([]);
  const [columnPreferenceExists, setColumnPreferenceExists] = useState(false);
  const [columnConfigError, setColumnConfigError] = useState("");
  const [columnMutationLoading, setColumnMutationLoading] = useState(false);
  const [editColumn, setEditColumn] = useState(null);
  const [renameDefaultColumn, setRenameDefaultColumn] = useState(null);
  const [deleteColumn, setDeleteColumn] = useState(null);

  const loadCustomColumns = async () => {
    const config = await getTaskManagerColumnConfig(Number(dashboardId), DEFAULT_TAB_ID);
    setCustomColumns(asList(config?.columns));
    setColumnPreferenceExists(!!config?.exists);
  };

  // Status mappings drive the display status and font colour of every status cell,
  // so they are loaded with the dashboard rather than only when Settings is opened.
  // Uses the status-mappings-only fetch (not getTaskManagerSettings) so a page load
  // does not pull the global email-mapping preference list just to colour cells.
  const loadStatusMappings = async () => {
    try {
      const mappings = await getTaskManagerStatusMappings(Number(dashboardId));
      setStatusMappings(asList(mappings));
    } catch (e) {
      // Non-fatal: statuses just render unmapped, in the default font colour.
      setStatusMappings([]);
    }
  };

  const loadPage = async (offset) => {
    const result = await getServices({ dashboard_id: Number(dashboardId), offset });
    const rows = asList(result?.services);
    setServices((prev) => (offset ? [...prev, ...rows] : rows));
    setNextOffset(result?.hasMore ? result.nextOffset : null);
    // Prefer the server total (constant across pages) so the KPI reflects the
    // full deliverable count, not just the pages fetched so far.
    if (typeof result?.total === "number") setTotalCount(result.total);
  };

  // Silently re-pull the first page after a successful cell edit so the grid
  // reflects the true persisted state (correct value source per column, resolved
  // tracking period, etc.) without a visible loading spinner. Failures are
  // swallowed - the optimistic in-place update already reflects the edit, and the
  // next natural load will reconcile.
  const refreshInBackground = async () => {
    try {
      await loadPage(0);
    } catch (e) {
      console.error("[TaskManager] background refresh failed", e);
    }
  };

  /**
   * Load everything the detail page needs from the first page onwards.
   * Centralizes all API integrations for this page so future ones (e.g. status,
   * trackers) can be added here and picked up by both the initial load and the
   * Refresh button. `cancelledRef` (optional) lets the mount effect abort stale
   * state updates on unmount.
   */
  const loadData = async () => {
    setLoading(true);
    setLoadError("");

    // Dashboard metadata (name/logo). Kept independent of the services fetch so
    // a services failure never blocks the title, and vice versa. Fetches this one
    // dashboard by id rather than listing them all. Timed separately from
    // services so we can see each call's latency.
    const dashboardStartedAt = Date.now();
    try {
      const match = await getDashboard(dashboardId);
      if (match && !match.error) setDashboard(match);
      captureMetrics(
        ACTION_METHODS.PAGE_LOAD_TIME,
        [
          {
            metricType: METRIC_TYPE.TIMER,
            metricName: "TaskManagerDashboardInfoLoadTime",
            metricValue: Date.now() - dashboardStartedAt,
            isMonitor: true,
          },
        ],
        SITE_SERVICES.TASK_MANAGER
      );
    } catch (e) {
      // Non-fatal for the title; keep whatever we already have (router state).
    }

    // Services list. Publish a Katal timer metric for how long the services
    // load takes, so we can monitor getServices latency from the UI.
    const startedAt = Date.now();
    try {
      await loadPage(0);
      captureMetrics(
        ACTION_METHODS.PAGE_LOAD_TIME,
        [
          {
            metricType: METRIC_TYPE.TIMER,
            metricName: "TaskManagerServicesLoadTime",
            metricValue: Date.now() - startedAt,
            isMonitor: true,
          },
        ],
        SITE_SERVICES.TASK_MANAGER
      );
    } catch (e) {
      setLoadError(e?.errors?.[0]?.message || e?.message || "Failed to load services.");
      setServices([]);
      setNextOffset(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Dedupe React 18 StrictMode's double effect invoke for the same id. The
    // ref alone is enough — do NOT also cancel on cleanup, or the (only) first
    // run's state writes get thrown away and nothing ever loads.
    if (fetchedDashboardId.current === dashboardId) return;
    fetchedDashboardId.current = dashboardId;
    loadData();
    loadStatusMappings();

    // App metadata (shared local cache) so the grid can show the app name per
    // deliverable, keyed by app_id.
    fetchAllApps()
      .then((apps) => {
        const map = {};
        (apps || []).forEach((a) => {
          map[String(a.app_id)] = a.app_name;
        });
        setAppsById(map);
      })
      .catch(() => setAppsById({}));
  }, [dashboardId]);

  // Resolve the current user's LDAP group memberships once, so non-admins also
  // see deliverables whose role is an LDAP group they belong to.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(getCachedLdaps(currentStage))
      .then((raw) => {
        if (cancelled) return;
        const list = typeof raw === "string" ? JSON.parse(raw || "[]") : raw || [];
        setLdapGroups(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setLdapGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStage]);

  // Resolve the stored logo object key to a short-lived viewable URL via Vault,
  // re-running whenever the dashboard's logo changes.
  useEffect(() => {
    let cancelled = false;
    const key = dashboard?.logo;
    if (!key) {
      setLogoUrl(null);
      return undefined;
    }
    getLogoViewUrl(key)
      .then((url) => {
        if (!cancelled) setLogoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLogoUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboard?.logo]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoadMore = async () => {
    if (nextOffset == null) return;
    setLoadingMore(true);
    setLoadError("");
    try {
      await loadPage(nextOffset);
    } catch (e) {
      setLoadError(e?.errors?.[0]?.message || e?.message || "Failed to load more services.");
    } finally {
      setLoadingMore(false);
    }
  };

  const applySettingsToDashboard = (baseDashboard, config, formValues = {}) => {
    const persistedDashboard = config?.dashboard || baseDashboard || {};
    return {
      ...(baseDashboard || {}),
      ...(config?.dashboard || {}),
      ...formValues,
      cadence: persistedDashboard?.cadence ?? formValues?.cadence ?? null,
      day: persistedDashboard?.day ?? formValues?.day ?? null,
      owners: Array.isArray(config?.owners)
        ? config.owners
        : asList(formValues?.owners ?? persistedDashboard?.owners),
      viewers: Array.isArray(config?.viewers)
        ? config.viewers
        : asList(formValues?.viewers ?? persistedDashboard?.viewers),
      notifications: {
        ...(baseDashboard?.notifications || {}),
        ...(formValues.notifications || {}),
        dailyDigest: !!(
          persistedDashboard?.daily_digest ??
          formValues?.notifications?.dailyDigest ??
          baseDashboard?.notifications?.dailyDigest
        ),
        notifyDuplicate: !!(
          persistedDashboard?.notify_duplicate ??
          formValues?.notifications?.notifyDuplicate ??
          baseDashboard?.notifications?.notifyDuplicate
        ),
      },
      statusMappings: asList(config?.statusMappings).map((mapping) => ({
        key: mapping.source_status,
        value: mapping.display_status,
        status: mapping.color,
      })),
    };
  };

  const handleOpenSettings = async () => {
    setSettingsOpen(true);
    setSettingsLoading(true);
    setSettingsError("");
    setSettingsDashboard(null);
    try {
      const config = await getTaskManagerSettings(Number(dashboardId));
      setSettingsPreferenceConfig(config);
      setSettingsDashboard(applySettingsToDashboard(dashboard, config));
    } catch (error) {
      setSettingsError(error?.message || "Failed to load settings.");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async (settings) => {
    setSettingsSaving(true);
    setSettingsError("");
    try {
      const savedConfig = await saveTaskManagerSettings({
        dashboardId: Number(dashboardId),
        userId,
        settings,
        existingConfig: settingsPreferenceConfig,
        dashboard,
      });
      const updatedDashboard = applySettingsToDashboard(dashboard, savedConfig, settings);
      setSettingsPreferenceConfig(savedConfig);
      setSettingsDashboard(updatedDashboard);
      // Keep the grid's status colours in step with the mapping just saved.
      setStatusMappings(asList(savedConfig?.statusMappings));
      setDashboard(updatedDashboard);
      setSettingsOpen(false);
    } catch (error) {
      setSettingsError(error?.message || "Failed to save settings.");
    } finally {
      setSettingsSaving(false);
    }
  };

  // ---- Saved grid view (column visibility / order / width) ----
  // Persisted per dashboard. Backed by localStorage for now; swap for a backend
  // mutation once the view-persistence API is available.
  const COLUMN_STATE_KEY = `taskManager.columnState.${dashboardId}`;

  // ag-grid moved getColumnState/applyColumnState from columnApi to gridApi in
  // newer versions; support both.
  const getGridColApi = () => gridRef.current?.columnApi || gridRef.current?.api || null;

  // ---- Row selection + bulk admin actions ----
  const onSelectionChanged = () => {
    const api = gridRef.current?.api;
    const rows = api ? api.getSelectedRows() : [];
    setSelectedRows(rows);
    // Expand the admin controls so the bulk actions are visible on selection.
    if (rows.length > 0 && canManage) setAdminControlsOpen(true);
  };

  const hasSelection = selectedRows.length > 0;

  // Hide / Unhide / Enable / Disable the checked deliverables. Backend wiring is
  // pending; for now this just clears the selection after acting.
  const handleBulkAction = (action) => {
    if (!hasSelection) return;
    // TODO: call the task-manager mutation for `action` with selectedRows once
    // the endpoint is available.
    gridRef.current?.api?.deselectAll?.();
    setSelectedRows([]);
  };

  // Persist a status-cell edit to task_manager.service_tracking (never to
  // service_attributes). The write echoes back the tracking_group getServices
  // resolved for the row and the cell's tracking_sub_group, so a correction lands
  // on the same period and sub-group the grid is showing. On failure the cell is
  // rolled back to its previous value.
  const handleTrackingStatusEdited = async (event, { source, serviceId, subGroup, rowKey }) => {
    if (savingRoleRows.has(rowKey)) return;
    const field = event.colDef?.field;
    const previousValue = event.oldValue;

    setSavingRoleRows((current) => new Set(current).add(rowKey));
    try {
      setLoadError("");
      const result = await manageTrackingUpdates({
        dashboard_id: Number(dashboardId),
        service_type: source,
        service_id: Number(serviceId),
        tracking_sub_group: subGroup,
        status: event.newValue,
        updating_alias: userId,
      });
      if (result?.statusCode !== 200) {
        throw new Error(result?.error || "Tracking update did not return 200.");
      }
      // The write succeeded, so drop the pending edit and let the row's real
      // tracking_status own the cell again.
      clearPendingStatus(event.data, subGroup);
      // Reflect the saved status in the row's tracking_status so the cell keeps
      // rendering the tracked value (and becomes read-only) without a reload.
      const savedSubGroup = String(subGroup || "").trim() || DEFAULT_TRACKING_SUB_GROUP;
      setServices((current) =>
        current.map((service) => {
          const serviceKey = serviceRowKey({
            source: service.source,
            appId: idOrNull(service.app_id),
            processId: processIdOf(service),
            flexId: flexIdOf(service),
            logicoProcessId: logicoProcessIdOf(service),
          });
          if (serviceKey !== rowKey) return service;
          return {
            ...service,
            tracking_status: { ...(service.tracking_status || {}), [savedSubGroup]: event.newValue },
          };
        })
      );
    } catch (error) {
      // Roll the cell back: drop the pending edit so the getter falls back to the
      // last tracked value.
      clearPendingStatus(event.data, subGroup);
      event.data[field] = previousValue;
      const editedColumn = event.column?.getColId?.() || event.colDef?.colId || field;
      event.api?.refreshCells?.({
        rowNodes: [event.node],
        columns: editedColumn ? [editedColumn] : undefined,
        force: true,
      });
      setLoadError(error?.message || "Failed to update status.");
    } finally {
      setSavingRoleRows((current) => {
        const next = new Set(current);
        next.delete(rowKey);
        return next;
      });
      // Reconcile the grid with the persisted state in the background.
      refreshInBackground();
    }
  };

  // Persist role edits through source-specific APIs and route other editable
  // cells through the placeholder service-attribute API.
  const handleRoleCellEdited = async (event) => {
    if (!canManage) return;
    const field = event.colDef?.field;
    const role = {
      preparer: "PREPARER",
      reviewer: "REVIEWER",
      approver: "APPROVER",
    }[field];
    const source = String(event.data?.source || "").toUpperCase();
    const rowKey = serviceRowKey(event.data);
    if (savingRoleRows.has(rowKey)) return;

    const serviceId =
      event.data?.processId ?? event.data?.flexId ?? event.data?.logicoProcessId;
    const customColumn = event.colDef?.context?.customColumn;
    const isGeneratedStatus = !!event.colDef?.context?.generatedDateStatus;
    // A tracking-backed status cell (the default Status column or a DATE column's
    // status) writes task_manager.service_tracking, not service_attributes.
    const isTrackingStatus = !!event.colDef?.context?.trackingStatus;
    const trackingSubGroup = event.colDef?.context?.trackingSubGroup;
    if (isTrackingStatus) {
      if (event.oldValue === event.newValue) return;
      await handleTrackingStatusEdited(event, {
        source,
        serviceId,
        subGroup: trackingSubGroup,
        rowKey,
      });
      return;
    }
    if (!role && (!customColumn || event.oldValue === event.newValue)) return;
    if (role) {
      const roleSignature = (rows) =>
        asList(rows)
          .map(
            (row) =>
              `${String(row?.alias || "").trim().toLowerCase()}|${String(
                row?.type || ""
              ).toUpperCase()}|${row?.column_index ?? ""}`
          )
          .sort()
          .join("\n");
      if (roleSignature(event.oldValue) === roleSignature(event.data?.[field])) return;
    }

    const attributeName = customColumn
      ? `${customColumn.name}${isGeneratedStatus ? " Status" : ""}`
      : null;
    const attributeKey = customColumn
      ? isGeneratedStatus
        ? `${customColumn.name.toUpperCase().replace(/\s+/g, "_")}_STATUS`
        : customColumn.name
      : null;

    const originalService = services.find((service) => {
      const serviceKey = serviceRowKey({
        source: service.source,
        appId: idOrNull(service.app_id),
        processId: processIdOf(service),
        flexId: flexIdOf(service),
        logicoProcessId: logicoProcessIdOf(service),
      });
      return serviceKey === rowKey;
    });
    const beforeAttributes = role ? null : { ...taskManagerAttributesOf(originalService) };
    const afterAttributes = role
      ? null
      : { ...beforeAttributes, [attributeKey]: event.newValue };

    const rowSnapshot = { ...event.data };
    delete rowSnapshot.roleSaving;
    const beforeSnapshot = role
      ? null
      : { ...rowSnapshot, task_manager_attributes: beforeAttributes };
    const afterSnapshot = role
      ? null
      : { ...rowSnapshot, task_manager_attributes: afterAttributes };

    const setLocalAttributes = (attributes) => {
      if (!attributeKey) return;
      setServices((current) =>
        current.map((service) => {
          const serviceKey = serviceRowKey({
            source: service.source,
            appId: idOrNull(service.app_id),
            processId: processIdOf(service),
            flexId: flexIdOf(service),
            logicoProcessId: logicoProcessIdOf(service),
          });
          return serviceKey === rowKey
            ? { ...service, task_manager_attributes: { ...attributes } }
            : service;
        })
      );
    };

    if (!role) setLocalAttributes(afterAttributes);
    setSavingRoleRows((current) => new Set(current).add(rowKey));
    try {
      setLoadError("");
      let result;
      if (role) {
        const commonInput = {
          dashboard_id: Number(dashboardId),
          app_id: Number(event.data?.appId),
          role,
          aliases: asList(event.data?.[field]),
          updating_alias: userId,
        };
        if (source === "FRISCO") {
          result = await updateProcessLiteViaTaskManager({
            ...commonInput,
            process_id: Number(event.data?.processId),
          });
        } else if (source === "FLEXICO") {
          result = await updateFlexicoTemplateViaTaskManager({
            ...commonInput,
            flex_id: Number(event.data?.flexId),
          });
        } else if (source === "LOGICO") {
          result = await updateLogicoProcessLiteViaTaskManager({
            ...commonInput,
            logico_process_id: Number(event.data?.logicoProcessId),
          });
        } else {
          throw new Error("Unsupported service type.");
        }
        if (result?.error) throw new Error(result.error);

        const savedAliases = asList(result?.aliases);
        setServices((current) =>
          current.map((service) => {
            const serviceKey = serviceRowKey({
              source: service.source,
              appId: idOrNull(service.app_id),
              processId: processIdOf(service),
              flexId: flexIdOf(service),
              logicoProcessId: logicoProcessIdOf(service),
            });
            if (serviceKey !== rowKey) return service;
            if (role === "PREPARER") {
              return { ...service, uploaders: savedAliases };
            }
            const columnIndex = role === "REVIEWER" ? 0 : 1;
            const otherApprovers = asList(service.approvers).filter(
              (alias) => Number(alias?.column_index) !== columnIndex
            );
            return { ...service, approvers: [...otherApprovers, ...savedAliases] };
          })
        );
      } else {
        // The UI decides where the value lands: a cadence-tracked column writes
        // the current period's tracking_custom_attribute via manageTrackingUpdates;
        // a plain column writes the dashboard-level service_attributes via
        // manageServiceAttributes. Both accept the same attribute payload shape.
        // DATE-type columns are always tracked by period, regardless of the
        // track_by_cadence flag, so they always route to manageTrackingUpdates.
        const isCadenceColumn =
          !!customColumn?.track_by_cadence || customColumn?.type === "DATE";
        const saveAttribute = isCadenceColumn
          ? manageTrackingUpdates
          : manageServiceAttributes;
        result = await saveAttribute({
          dashboard_id: Number(dashboardId),
          service_type: source,
          app_id: Number(event.data?.appId),
          service_id: Number(serviceId),
          attribute: {
            key: attributeKey,
            name: attributeName,
            type: customColumn?.type,
            is_status: isGeneratedStatus,
            track_by_cadence: isCadenceColumn,
            value: event.newValue,
          },
          // The backend resolves the cadence period itself from the dashboard
          // cadence (same as the status write, which also sends no period and lets
          // the API default to the current PST period). No tracking_group needed.
          updating_alias: userId,
        });
        if (result?.statusCode !== 200) {
          throw new Error(result?.error || "Service attribute API did not return 200.");
        }
        // Audit from the UI now that the value persisted. The save API no longer
        // ships/audits the before/after row snapshots.
        try {
          await createServiceAttributeAudit(beforeSnapshot, afterSnapshot);
        } catch (auditError) {
          // Do not fail/revert the saved edit if only the audit log write fails.
          console.error("[TaskManager] service attribute audit failed", auditError);
        }
      }
    } catch (error) {
      if (role) {
        event.data[field] = event.oldValue;
      } else {
        setLocalAttributes(beforeAttributes);
        event.data.task_manager_attributes = { ...beforeAttributes };
      }
      const editedColumn =
        event.column?.getColId?.() || event.colDef?.colId || field;
      event.api?.refreshCells?.({
        rowNodes: [event.node],
        columns: editedColumn ? [editedColumn] : undefined,
        force: true,
      });
      setLoadError(error?.message || "Failed to save grid value.");
    } finally {
      setSavingRoleRows((current) => {
        const next = new Set(current);
        next.delete(rowKey);
        return next;
      });
      // Reconcile the grid with the persisted state in the background so every
      // column shows its true value from the correct source after the write.
      refreshInBackground();
    }
  };

  // Normalize column state to just the parts a "view" cares about (visibility +
  // order) so resize noise doesn't mark the view dirty.
  const normalizeColState = (state) =>
    JSON.stringify((state || []).map((c) => ({ colId: c.colId, hide: !!c.hide })));

  const currentColStateString = () => {
    const api = getGridColApi();
    return normalizeColState(api?.getColumnState?.());
  };

  // Recompute whether the current columns differ from the saved/default
  // baseline. When they do, enable "Save view" and auto-open the admin panel.
  const recomputeViewDirty = () => {
    const dirty = currentColStateString() !== baselineColStateRef.current;
    setViewDirty(dirty);
    if (dirty) setAdminControlsOpen(true);
  };

  const applySavedColumnState = () => {
    const api = getGridColApi();
    try {
      const saved = localStorage.getItem(COLUMN_STATE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        api?.applyColumnState?.({ state, applyOrder: true });
      }
    } catch (e) {
      /* ignore malformed saved state */
    }
    // Whatever is applied now (saved or default) becomes the clean baseline.
    baselineColStateRef.current = currentColStateString();
    setViewDirty(false);
  };

  const handleGridReady = () => {
    applySavedColumnState();
    setColumnConfigError("");
    loadCustomColumns().catch((error) => {
      setColumnConfigError(error?.message || "Failed to load custom columns.");
    });
  };

  // Column definitions arrive asynchronously from GenericPreference. Reapply
  // any saved view after AG Grid installs the newly appended definitions.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    applySavedColumnState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customColumns]);

  const handleSaveView = () => {
    const api = getGridColApi();
    const state = api?.getColumnState?.();
    if (!state) return;
    try {
      localStorage.setItem(COLUMN_STATE_KEY, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable — ignore */
    }
    baselineColStateRef.current = normalizeColState(state);
    setViewDirty(false);
  };

  const handleResetView = () => {
    const api = getGridColApi();
    api?.resetColumnState?.();
    try {
      localStorage.removeItem(COLUMN_STATE_KEY);
    } catch (e) {
      /* ignore */
    }
    baselineColStateRef.current = currentColStateString();
    setViewDirty(false);
  };

  const rowData = useMemo(
    () => {
      // Identities that count as "mine": the logged-in user plus any LDAP groups
      // they belong to (aliases are stored as bare logins / group names).
      const myIdentities = new Set(
        [userId, ...asList(userLDAPS), ...asList(ldapGroups)]
          .filter(Boolean)
          .map((s) => String(s).toLowerCase())
      );
      const isMine = (row) =>
        [
          ...aliasesOf(row.preparer),
          ...aliasesOf(row.approver),
          ...aliasesOf(row.reviewer),
        ].some((a) => myIdentities.has(String(a).toLowerCase()));

      const rows = services.map((svc) => {
        const appId = svc.app_id ?? svc.appId ?? null;
        return {
          deliverable: svc.deliverable || "",
          deliverableHref: deliverableHrefOf(svc),
          disabled: isDisabledOf(svc),
          source: svc.source || "",
          processId: processIdOf(svc),
          flexId: flexIdOf(svc),
          logicoProcessId: logicoProcessIdOf(svc),
          preparer: preparersOf(svc),
          approver: approverRoleOf(svc, 1),
          reviewer: approverRoleOf(svc, 0),
          appId: idOrNull(appId),
          appName: appId != null ? appsById[String(appId)] || String(appId) : "",
          serviceType: svc.source || "",
          // Tracking status keyed by tracking_sub_group. The default Status column
          // reads UNKNOWN; a DATE column's status reads its own date type.
          tracking_status: trackingStatusOf(svc),
          // file_id per sub-group, used to link a status to its transaction page.
          tracking_files: trackingFilesOf(svc),
          // The period getServices resolved, echoed back on a status write so the
          // update lands on the same tracking_group the grid is showing.
          tracking_group: svc?.tracking_group ?? null,
          // getServices returns the two value sources separately (service_attributes
          // and tracking_custom_attributes for the current period). Pick per column
          // by its track_by_cadence flag so a cadence column never shows a stale
          // service_attributes value and vice versa.
          task_manager_attributes: resolveCustomColumnValues(svc, customColumns),
          roleSaving: savingRoleRows.has(
            serviceRowKey({
              source: svc.source,
              appId: idOrNull(appId),
              processId: processIdOf(svc),
              flexId: flexIdOf(svc),
              logicoProcessId: logicoProcessIdOf(svc),
            })
          ),
        };
      });

      // Admins see everything; non-admins only the deliverables they preparer /
      // review / approve.
      return canManage ? rows : rows.filter(isMine);
    },
    [services, appsById, canManage, userId, userLDAPS, ldapGroups, savingRoleRows]
  );

  // Render the deliverable as an in-app link when we can route it (FRISCO ->
  // process upload page, Flexico -> flexico execution page); otherwise plain
  // text. Uses history.push (with a real href for open-in-new-tab support)
  // instead of a full page reload.
  // Render the deliverable like the CRA deliverables dashboard: the name as a
  // small link (FRISCO -> process upload page, Flexico -> flexico execution
  // page), with a service tag underneath ("Automated" for Flexico).
  const DeliverableCell = (params) => {
    const value = params.value || "";
    const data = params.data || {};
    const href = data.deliverableHref;
    // Always render the deliverable name as a link. When we have a routable href
    // (FRISCO process / Flexico upload page), clicking navigates in-app.
    // Otherwise (backend not yet returning ids) it renders as a link but is a
    // no-op on click — placeholder until the ids ship.
    const nameNode = (
      <Link
        href={href || "#"}
        variant="primary"
        onFollow={(e) => {
          if (!href) {
            if (e?.preventDefault) e.preventDefault();
            return;
          }
          // Cloudscape Link fires onFollow for a normal click; take over routing
          // in-app. Modified clicks (new tab/window) keep the native href.
          if (e?.detail?.ctrlKey || e?.detail?.metaKey || e?.detail?.shiftKey || e?.detail?.button === 1) {
            return;
          }
          if (e?.preventDefault) e.preventDefault();
          history.push(href);
        }}
      >
        <span className="small_values">{value}</span>
      </Link>
    );

    return (
      <Box>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {/* Admin-only gear to the left of the deliverable name -> opens the
              per-row settings dialog. */}
          {canManage && (
            <Button
              variant="inline-icon"
              iconName="settings"
              ariaLabel="Row settings"
              onClick={() => setRowSettingsRow(data)}
            />
          )}
          <SpaceBetween direction="vertical" size="xxs">
            {nameNode}

            {/* Disabled badge for disabled deliverables. */}
            {data.disabled && (
              <span className="smaller_values grey_badge">Disabled</span>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Box>
    );
  };

  /**
   * Tracked status cell. When the status came from a transaction we can point at,
   * it renders as an in-app link to that transaction: the file details page for
   * FRISCO / FlexiCo, the process history page for LOGICO. With no status, or no
   * resolvable target, it renders as plain text.
   *
   * The sub-group is supplied per column via cellRendererParams.subGroup.
   */
  // Dropdown choices for the status columns: the display values configured in the
  // Control Panel "Status Mapping Configuration". Deduped, order preserved. Empty
  // when nothing is configured, in which case the status editor falls back to plain
  // free text.
  const statusMappingOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    asList(statusMappings).forEach((mapping) => {
      const value = String(mapping?.display_status || "").trim();
      const key = value.toLowerCase();
      if (value && !seen.has(key)) {
        seen.add(key);
        options.push(value);
      }
    });
    return options;
  }, [statusMappings]);

  const statusMappingIndex = useMemo(
    () => indexStatusMappings(statusMappings),
    [statusMappings]
  );

  // Editor config shared by every status column (the default Status column and the
  // per-DATE-column "<name> Status" columns). When the Control Panel has status
  // mappings configured the cell is a dropdown of those display values; with none
  // configured it falls back to a free-text editor.
  const statusEditorProps = useMemo(() => {
    // With status mappings configured the cell is a dropdown of those display
    // values; with none configured it falls back to a free-text editor.
    if (statusMappingOptions.length > 0) {
      return {
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: statusMappingOptions },
      };
    }
    return { cellEditor: "agTextCellEditor" };
  }, [statusMappingOptions]);

  // Resolve a raw tracking status to the display status shown in the grid. Used
  // by the status columns' valueGetter so AG Grid filter/sort/quick-filter operate
  // on the label the user actually sees; unmapped statuses fall back to the raw
  // value (which is also what is rendered).
  const displayStatusFor = (row, subGroup) => {
    const rawValue = String(trackingStatusFor(row, subGroup) || "").trim();
    if (!rawValue) return "";
    return statusMappingIndex[rawValue.toLowerCase()]?.displayStatus || rawValue;
  };

  const StatusCell = (params) => {
    // The column's valueGetter already resolves to the mapped display status (so
    // AG Grid filter/sort/quick-filter match what's rendered). Colour and link,
    // however, key off the raw tracking status from the row, so resolve that here
    // rather than re-mapping params.value.
    const rawValue = String(trackingStatusFor(params.data, params.subGroup) || "").trim();
    const value = params.value || rawValue;
    if (!value) return null;

    // Colour the text with the raw status's mapped colour, if any. Unmapped
    // statuses render in the default font colour.
    const mapping = rawValue ? statusMappingIndex[rawValue.toLowerCase()] : undefined;
    const statusStyle = mapping?.color ? { color: mapping.color } : undefined;

    const href = transactionHrefOf(params.data, params.subGroup);
    if (!href) {
      return (
        <span className="small_values" style={statusStyle}>
          {value}
        </span>
      );
    }

    return (
      <Link
        href={href}
        variant="primary"
        onFollow={(e) => {
          // Let modified clicks (new tab/window) use the native href.
          if (
            e?.detail?.ctrlKey ||
            e?.detail?.metaKey ||
            e?.detail?.shiftKey ||
            e?.detail?.button === 1
          ) {
            return;
          }
          if (e?.preventDefault) e.preventDefault();
          history.push(href);
        }}
      >
        <span className="small_values" style={statusStyle}>
          {value}
        </span>
      </Link>
    );
  };

  // Render the app name as an in-app link to the app details page
  // (/App/:id). Falls back to plain text when the row has no app id.
  const AppCell = (params) => {
    const value = params.value || "";
    const appId = params.data?.appId;
    if (!appId) {
      return <span className="small_values">{value}</span>;
    }
    const href = `/App/${appId}`;
    return (
      <Link
        href={href}
        variant="primary"
        onFollow={(e) => {
          // Modified clicks (new tab/window) keep the native href.
          if (e?.detail?.ctrlKey || e?.detail?.metaKey || e?.detail?.shiftKey || e?.detail?.button === 1) {
            return;
          }
          if (e?.preventDefault) e.preventDefault();
          history.push(href);
        }}
      >
        <span className="small_values">{value}</span>
      </Link>
    );
  };

  const columnNameExists = (candidate, ignoredCustomName = null, ignoredField = null) => {
    const normalizedCandidate = candidate.trim().toLowerCase();
    const normalizedIgnored = ignoredCustomName?.trim().toLowerCase();
    const gridDefinitions = gridRef.current?.api?.getColumnDefs?.();
    const definitions = gridDefinitions?.length ? gridDefinitions : columnDefs;
    return definitions.some((definition) => {
      if (ignoredField && definition.field === ignoredField) return false;
      const customName = definition.context?.customColumn?.name?.trim().toLowerCase();
      if (normalizedIgnored && customName === normalizedIgnored) return false;
      return [definition.headerName, definition.field]
        .filter(Boolean)
        .some((value) => String(value).trim().toLowerCase() === normalizedCandidate);
    });
  };

  const defaultColumnConfigFor = (field) =>
    customColumns.find((column) => column.type === "DEFAULT" && column.field === field);
  const defaultHeaderName = (field, fallback) =>
    defaultColumnConfigFor(field)?.name || fallback;

  const persistCustomColumns = async (columns) => {
    const saved = await saveTaskManagerColumnConfig({
      dashboardId: Number(dashboardId),
      tabId: DEFAULT_TAB_ID,
      columns,
      userId,
      preferenceExists: columnPreferenceExists,
    });
    const savedColumns = asList(saved?.columns);
    setCustomColumns(savedColumns);
    setColumnPreferenceExists(true);
    return savedColumns;
  };

  const createColumnAudit = (action, before, after) =>
    new Promise((resolve, reject) => {
      createAuditLog(
        `TASK_MANAGER#${dashboardId}`,
        `COLUMN#${Date.now()}`,
        `Task Manager custom column ${action.toLowerCase()}`,
        before == null ? null : JSON.stringify(before),
        after == null ? null : JSON.stringify(after),
        action,
        userId,
        resolve,
        (error) => reject(error instanceof Error ? error : new Error("Audit log request failed"))
      );
    });

  // Audit a service-attribute / cadence value edit from the UI. The save APIs
  // (manageServiceAttributes / manageTrackingUpdates) only persist the value now;
  // the before/after row snapshots are logged here instead of being shipped to the
  // Lambda on every write.
  const createServiceAttributeAudit = (before, after) =>
    new Promise((resolve, reject) => {
      createAuditLog(
        `TASK_MANAGER#${dashboardId}`,
        `SERVICE_ATTR#${Date.now()}`,
        "Task Manager service attribute updated",
        before == null ? null : JSON.stringify(before),
        after == null ? null : JSON.stringify(after),
        "SERVICE_ATTRIBUTE_UPDATED",
        userId,
        resolve,
        (error) => reject(error instanceof Error ? error : new Error("Audit log request failed"))
      );
    });

  const handleAddColumn = async (column) => {
    setColumnConfigError("");

    const name = column?.name?.trim();
    if (!name) return false;
    if (columnNameExists(name)) {
      setColumnConfigError(`A column named "${name}" already exists.`);
      return false;
    }

    const nextOrder = customColumns
      .filter((existing) => existing?.type !== "DEFAULT")
      .reduce(
        (maxOrder, existing) => Math.max(maxOrder, Number(existing?.order) || 0),
        -1
      ) + 1;
    const nextColumn = {
      name,
      type: column.type,
      is_visible: true,
      order: nextOrder,
      track_by_cadence: !!column.trackByCadence,
      ...(column.type === "DATE"
        ? {
            ...(column.dateType?.trim() ? { due_date_type: column.dateType.trim() } : {}),
            required_when_triggered: !!column.requiredWhenTriggered,
            include_in_digest: !!column.includeInDigest,
          }
        : {}),
      ...(column.type === "DROPDOWN" && Array.isArray(column.values)
        ? { values: column.values }
        : {}),
    };
    const nextColumns = [...customColumns, nextColumn];

    setColumnMutationLoading(true);
    try {
      await createColumnAudit("ADD_COLUMN", customColumns, nextColumns);
      await persistCustomColumns(nextColumns);
      setAddColumnOpen(false);
      return true;
    } catch (error) {
      setColumnConfigError(error?.message || "Failed to audit or save custom column.");
      return false;
    } finally {
      setColumnMutationLoading(false);
    }
  };

  const handleEditColumn = async (columnConfig) => {
    const original = editColumn;
    const name = columnConfig?.name?.trim();
    if (!original || !name) return false;

    setColumnConfigError("");
    if (columnNameExists(name, original.name)) {
      setColumnConfigError(`A column named "${name}" already exists.`);
      return false;
    }

    const unchangedConfig = { ...original };
    delete unchangedConfig.due_date_type;
    delete unchangedConfig.required_when_triggered;
    delete unchangedConfig.include_in_digest;
    delete unchangedConfig.track_by_cadence;
    delete unchangedConfig.values;
    const updated = {
      ...unchangedConfig,
      name,
      type: columnConfig.type,
      track_by_cadence: !!columnConfig.trackByCadence,
      ...(columnConfig.type === "DATE"
        ? {
            ...(columnConfig.dateType?.trim()
              ? { due_date_type: columnConfig.dateType.trim() }
              : {}),
            required_when_triggered: !!columnConfig.requiredWhenTriggered,
            include_in_digest: !!columnConfig.includeInDigest,
          }
        : {}),
      ...(columnConfig.type === "DROPDOWN"
        ? { values: asList(columnConfig.values) }
        : {}),
    };
    const nextColumns = customColumns.map((column) =>
      column.name.trim().toLowerCase() === original.name.trim().toLowerCase()
        ? updated
        : column
    );

    setColumnMutationLoading(true);
    try {
      await createColumnAudit("EDIT_COLUMN", customColumns, nextColumns);
      await persistCustomColumns(nextColumns);
      setEditColumn(null);
      return true;
    } catch (error) {
      setColumnConfigError(error?.message || "Failed to audit or update custom column.");
      return false;
    } finally {
      setColumnMutationLoading(false);
    }
  };

  const handleRenameDefaultColumn = async () => {
    const field = renameDefaultColumn?.field;
    const name = renameDefaultColumn?.name?.trim();
    if (!field || !name) return;

    setColumnConfigError("");
    if (columnNameExists(name, null, field)) {
      setColumnConfigError(`A column named "${name}" already exists.`);
      return;
    }

    const existing = defaultColumnConfigFor(field);
    const updated = {
      ...(existing || {}),
      name,
      type: "DEFAULT",
      field,
      is_visible: true,
      order: existing?.order ?? customColumns.length,
    };
    const nextColumns = existing
      ? customColumns.map((column) =>
          column.type === "DEFAULT" && column.field === field ? updated : column
        )
      : [...customColumns, updated];

    setColumnMutationLoading(true);
    try {
      await createColumnAudit("RENAME_DEFAULT_COLUMN", customColumns, nextColumns);
      await persistCustomColumns(nextColumns);
      setRenameDefaultColumn(null);
    } catch (error) {
      setColumnConfigError(error?.message || "Failed to audit or rename default column.");
    } finally {
      setColumnMutationLoading(false);
    }
  };

  const handleDeleteColumn = async () => {
    if (!deleteColumn) return;
    setColumnConfigError("");
    const deleted = deleteColumn;
    const nextColumns = customColumns
      .filter(
        (column) =>
          column.name.trim().toLowerCase() !== deleted.name.trim().toLowerCase()
      )
      .map((column, order) => ({ ...column, order }));

    setColumnMutationLoading(true);
    try {
      await createColumnAudit("DELETE_COLUMN", customColumns, nextColumns);
      await persistCustomColumns(nextColumns);
      setDeleteColumn(null);
    } catch (error) {
      setColumnConfigError(error?.message || "Failed to audit or delete custom column.");
    } finally {
      setColumnMutationLoading(false);
    }
  };

  const getMainMenuItems = (params) => {
    const columnDefinition = params.column?.getColDef?.() || params.column?.colDef;
    const customColumn = columnDefinition?.context?.customColumn;
    const defaultColumn = columnDefinition?.context?.defaultColumn;
    if (!canManage) return params.defaultItems;

    if (defaultColumn) {
      return [
        ...params.defaultItems,
        "separator",
        {
          name: "Rename column",
          action: () => {
            setColumnConfigError("");
            setRenameDefaultColumn({
              ...defaultColumn,
              name: columnDefinition.headerName || defaultColumn.defaultName,
            });
          },
        },
      ];
    }

    if (!customColumn) return params.defaultItems;
    return [
      ...params.defaultItems,
      "separator",
      {
        name: "Edit column",
        action: () => {
          setColumnConfigError("");
          setEditColumn(customColumn);
        },
      },
      {
        name: "Delete column",
        action: () => {
          setColumnConfigError("");
          setDeleteColumn(customColumn);
        },
      },
    ];
  };

  const columnDefs = useMemo(
    () => {
      const baseColumns = [
      {
        headerName: "",
        colId: "__select",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        pinned: "left",
        resizable: false,
        sortable: false,
        filter: false,
        suppressMenu: true,
        lockPosition: true,
        suppressSizeToFit: true,
      },
      // Deliverable / App / Service Type stay read-only. The alias role columns
      // (Preparer / Reviewer / Approver) are editable for admins — type a
      // comma-separated list of aliases.
      { headerName: defaultHeaderName("deliverable", "Deliverable"), field: "deliverable", editable: false, context: { defaultColumn: { field: "deliverable", defaultName: "Deliverable" } }, cellRenderer: DeliverableCell, autoHeight: true, flex: 2, minWidth: 260 },
      {
        headerName: defaultHeaderName("preparer", "Preparer"),
        field: "preparer",
        context: { defaultColumn: { field: "preparer", defaultName: "Preparer" } },
        cellRenderer: RoleCell,
        editable: (params) => canManage && !params.data?.roleSaving,
        cellEditor: RoleAliasCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { role: "preparer" },
        valueSetter: (p) => {
          p.data.preparer = asList(p.newValue);
          return true;
        },
        // Filter/search on the alias strings, not the raw {alias,type} objects.
        filterValueGetter: (p) => aliasesOf(p.data?.preparer),
        getQuickFilterText: roleQuickFilterText,
        autoHeight: true,
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: defaultHeaderName("reviewer", "Reviewer"),
        field: "reviewer",
        context: { defaultColumn: { field: "reviewer", defaultName: "Reviewer" } },
        cellRenderer: RoleCell,
        editable: (params) => canManage && !params.data?.roleSaving,
        cellEditor: RoleAliasCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { role: "reviewer", columnIndex: 0 },
        valueSetter: (p) => {
          p.data.reviewer = asList(p.newValue);
          return true;
        },
        filterValueGetter: (p) => aliasesOf(p.data?.reviewer),
        getQuickFilterText: roleQuickFilterText,
        autoHeight: true,
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: defaultHeaderName("approver", "Approver"),
        field: "approver",
        context: { defaultColumn: { field: "approver", defaultName: "Approver" } },
        cellRenderer: RoleCell,
        editable: (params) => canManage && !params.data?.roleSaving,
        cellEditor: RoleAliasCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { role: "approver", columnIndex: 1 },
        valueSetter: (p) => {
          p.data.approver = asList(p.newValue);
          return true;
        },
        filterValueGetter: (p) => aliasesOf(p.data?.approver),
        getQuickFilterText: roleQuickFilterText,
        autoHeight: true,
        flex: 1,
        minWidth: 160,
      },
      { headerName: defaultHeaderName("appName", "App"), field: "appName", editable: false, context: { defaultColumn: { field: "appName", defaultName: "App" } }, cellRenderer: AppCell, flex: 1, minWidth: 140 },
      { headerName: defaultHeaderName("serviceType", "Service Type"), field: "serviceType", editable: false, context: { defaultColumn: { field: "serviceType", defaultName: "Service Type" } }, flex: 1, minWidth: 130 },
      {
        // Tracking status for the default (UNKNOWN) sub-group. Services whose
        // sub-group is a configured DATE column surface their status on that
        // column's own status column instead, so this stays blank for them.
        headerName: defaultHeaderName("status", "Status"),
        field: "status",
        // trackingStatus + trackingSubGroup mark this as a tracking-backed status
        // cell: an edit writes task_manager.service_tracking (via manageService
        // tracking), not task_manager.service_attributes.
        context: {
          defaultColumn: { field: "status", defaultName: "Status" },
          trackingStatus: true,
          trackingSubGroup: DEFAULT_TRACKING_SUB_GROUP,
        },
        // Status is always editable; the edit writes task_manager.service_tracking
        // for this period and sub-group whether or not a tracking row already exists.
        editable: (params) => canManage && !params.data?.roleSaving,
        ...statusEditorProps,
        // Return the mapped display status so filter/sort/quick-filter match the
        // rendered label; StatusCell resolves colour/link from the raw status. Fall
        // back to the manually maintained attribute when no tracking row exists.
        valueGetter: (params) => {
          const pending = pendingStatusOf(params.data, DEFAULT_TRACKING_SUB_GROUP);
          if (pending !== undefined) return pending;
          return (
            displayStatusFor(params.data, DEFAULT_TRACKING_SUB_GROUP) ||
            params.data?.task_manager_attributes?.STATUS ||
            ""
          );
        },
        cellRenderer: (params) =>
          trackingStatusFor(params.data, DEFAULT_TRACKING_SUB_GROUP)
            ? StatusCell({ ...params, subGroup: DEFAULT_TRACKING_SUB_GROUP })
            : params.value ?? "",
        // Stash the picked value so the getter reflects it and the change commits;
        // handleTrackingStatusEdited persists event.newValue to service_tracking.
        valueSetter: (params) =>
          setPendingStatus(params.data, DEFAULT_TRACKING_SUB_GROUP, params.newValue),
        flex: 1,
        minWidth: 140,
      },
      ];

      const persistedColumns = [...customColumns]
        .filter(
          (column) =>
            column?.type !== "DEFAULT" &&
            typeof column?.name === "string" &&
            column.name.trim()
        )
        .sort((left, right) => (Number(left.order) || 0) - (Number(right.order) || 0))
        .flatMap((column) => {
          const valueColumn = {
            headerName: column.name,
            colId: `custom:${column.name}`,
            hide: column.is_visible === false,
            filter:
              column.type === "NUMBER"
                ? "agNumberColumnFilter"
                : column.type === "DATE"
                  ? "agDateColumnFilter"
                  : true,
            flex: 1,
            minWidth: 140,
            editable: (params) => canManage && !params.data?.roleSaving,
            cellEditor:
              column.type === "DROPDOWN"
                ? "agSelectCellEditor"
                : column.type === "NUMBER"
                  ? "agNumberCellEditor"
                  : column.type === "DATE"
                    ? "agDateStringCellEditor"
                    : "agTextCellEditor",
            cellEditorParams:
              column.type === "DROPDOWN"
                ? { values: asList(column.values) }
                : undefined,
            // A custom column's value (TEXT / NUMBER / DROPDOWN / DATE) is admin
            // input, not transaction data, so it is a plain attribute stored in
            // task_manager.service_attributes keyed by the column name.
            // handleRoleCellEdited persists it (no trackingStatus flag). Only the
            // generated "<name> Status" companion column below is tracking-backed.
            context: { customColumn: column },
            valueGetter: (params) =>
              params.data?.task_manager_attributes?.[column.name] ?? "",
            valueSetter: (params) => {
              params.data.task_manager_attributes = {
                ...(params.data.task_manager_attributes || {}),
                [column.name]: params.newValue,
              };
              return true;
            },
          };

          if (column.type !== "DATE") return [valueColumn];

          const statusName = String(column.name).trim();
          const statusAttribute = `${statusName.toUpperCase().replace(/\s+/g, "_")}_STATUS`;
          const statusColumn = {
            headerName: `${statusName} Status`,
            colId: `custom-status:${column.name}`,
            hide: column.is_visible === false,
            filter: true,
            flex: 1,
            minWidth: 140,
            // Admins can always edit, whether or not a tracking row exists; the
            // edit writes service_tracking for this column's sub-group.
            editable: (params) => canManage && !params.data?.roleSaving,
            // Dropdown of the Control Panel status-mapping values, or free text when
            // none are configured.
            ...statusEditorProps,
            // A DATE column's status is tracking-backed too; its tracking_sub_group
            // is the column's date type. Editing writes service_tracking.
            context: {
              customColumn: column,
              generatedDateStatus: true,
              trackingStatus: true,
              trackingSubGroup: column.due_date_type,
            },
            // The listener stores this column's tracking row under its date type as
            // the tracking_sub_group, so prefer that (as its mapped display status,
            // so filter/sort/quick-filter match the rendered label); fall back to
            // the manually maintained attribute when no tracking row exists yet.
            valueGetter: (params) => {
              const pending = pendingStatusOf(params.data, column.due_date_type);
              if (pending !== undefined) return pending;
              return (
                displayStatusFor(params.data, column.due_date_type) ||
                params.data?.task_manager_attributes?.[statusAttribute] ||
                ""
              );
            },
            // Link to the transaction only when this status came from a tracking
            // row; otherwise the plain (editable) attribute value is shown.
            cellRenderer: (params) =>
              trackingStatusFor(params.data, column.due_date_type)
                ? StatusCell({ ...params, subGroup: column.due_date_type })
                : params.value ?? "",
            // Stash the picked value so the getter reflects it and the change
            // commits; handleTrackingStatusEdited persists it to service_tracking.
            valueSetter: (params) =>
              setPendingStatus(params.data, column.due_date_type, params.newValue),
          };
          return [valueColumn, statusColumn];
        });

      return [...baseColumns, ...persistedColumns];
    },
    // statusMappingIndex re-renders the status cells with their mapped label and
    // colour once the mappings load (or are re-saved); statusEditorProps re-applies
    // the dropdown/free-text editor from the same mappings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, customColumns, statusMappingIndex, statusEditorProps]
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true }),
    []
  );

  const content = (
    <ContentLayout defaultPadding={true}>
      <SpaceBetween size="m">
        <SummaryBar
          name={dashboard?.name || `Dashboard ${dashboardId}`}
          logoUrl={logoUrl}
          // Admins see every deliverable, so use the server total (accurate
          // before all pages load). Non-admins see only their scoped rows, for
          // which the loaded row count is the right figure.
          allDeliverables={canManage ? (totalCount ?? rowData.length) : rowData.length}
          notDue={0}
          dueToday={0}
          overdue={0}
          canManage={canManage}
          canEdit={canEditDashboard}
          onToggleAdmin={() => setAdminControlsOpen((o) => !o)}
          onEdit={() => setEditOpen(true)}
        />

        {canManage && adminControlsOpen && (
          <Container>
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Box variant="h4" padding="n">
                Admin controls
              </Box>
              <Button iconName="settings" onClick={handleOpenSettings}>
                Settings
              </Button>
              <Button
                iconName="file-open"
                onClick={() => history.push(getTaskManagerAuditRoute(dashboardId))}
              >
                Audit logs
              </Button>
              <Button
                iconName="add-plus"
                onClick={() => {
                  setColumnConfigError("");
                  setAddColumnOpen(true);
                }}
              >
                Add column
              </Button>
              <Button iconName="check" onClick={handleSaveView} disabled={!viewDirty}>
                Save view
              </Button>
              <Button iconName="undo" onClick={handleResetView}>
                Reset view
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("HIDE")}>
                Hide
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("UNHIDE")}>
                Unhide
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("ENABLE")}>
                Enable
              </Button>
              <Button disabled={!hasSelection} onClick={() => handleBulkAction("DISABLE")}>
                Disable
              </Button>
              <Button variant="primary" iconName="add-plus"
                onClick={() => setAdhocOpen(true)}>
                Adhoc task
              </Button>
            </SpaceBetween>
          </Container>
        )}

        {loadError && (
          <Alert type="error" header="Couldn't load services" dismissible onDismiss={() => setLoadError("")}>
            {loadError}
          </Alert>
        )}

        {columnConfigError && (
          <Alert
            type="error"
            header="Couldn't update custom columns"
            dismissible
            onDismiss={() => setColumnConfigError("")}
          >
            {columnConfigError}
          </Alert>
        )}

        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <div style={{ flex: 1, maxWidth: 800 }}>
            <Input
              type="search"
              value={quickFilter}
              placeholder="Search services..."
              onChange={({ detail }) => setQuickFilter(detail.value)}
            />
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Button iconName="refresh" onClick={handleRefresh} loading={refreshing}>
              Refresh
            </Button>
            <Button iconName="arrow-left" onClick={() => history.push(TASK_MANAGER_ROUTE)}>
              Back
            </Button>
          </div>
        </div>

        <div className="ag-theme-alpine" style={{ width: "100%", height: "70vh" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={quickFilter}
            animateRows={true}
            rowHeight={32}
            rowSelection="multiple"
            isRowSelectable={(node) => !node.data?.roleSaving}
            getRowStyle={(params) =>
              params.data?.roleSaving
                ? { opacity: 0.55, pointerEvents: "none", cursor: "wait" }
                : undefined
            }
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            onGridReady={handleGridReady}
            getMainMenuItems={getMainMenuItems}
            onSelectionChanged={onSelectionChanged}
            onDisplayedColumnsChanged={recomputeViewDirty}
            onCellValueChanged={handleRoleCellEdited}
            overlayNoRowsTemplate={
              loading
                ? "Loading services..."
                : "No services are associated with this dashboard's apps yet."
            }
          />
        </div>

        {nextOffset != null && (
          <Box textAlign="center">
            <Button onClick={handleLoadMore} loading={loadingMore}>
              Load more
            </Button>
          </Box>
        )}
      </SpaceBetween>

      {/* Dashboard-level AdHoc task manager (create / list / confirm). */}
      <Modal
        visible={adhocOpen}
        onDismiss={() => setAdhocOpen(false)}
        header="Adhoc task manager"
        size="large"
        footer={
          <Box float="right">
            <Button variant="link" onClick={() => setAdhocOpen(false)}>
              Close
            </Button>
          </Box>
        }
      >
        {adhocOpen && <AdhocTasks entityType="DASHBOARD" entityId={dashboardId} />}
      </Modal>

      <ControlPanelModal
        visible={settingsOpen}
        dashboard={settingsDashboard || dashboard}
        loading={settingsLoading}
        saving={settingsSaving}
        error={settingsError}
        onDismiss={() => {
          if (!settingsSaving) {
            setSettingsOpen(false);
            setSettingsError("");
          }
        }}
        onSave={handleSaveSettings}
      />

      <RowSettingsModal
        visible={rowSettingsRow != null}
        row={rowSettingsRow}
        onDismiss={() => setRowSettingsRow(null)}
        onSave={() => setRowSettingsRow(null)}
      />

      <CreateTaskManagerModal
        visible={editOpen}
        dashboard={dashboard}
        onDismiss={() => setEditOpen(false)}
        onSaved={(updated) => {
          // Merge the saved fields so the title/logo update immediately, then
          // refresh the services (the app associations may have changed).
          setDashboard((prev) => ({ ...(prev || {}), ...(updated || {}) }));
          setEditOpen(false);
          loadData();
        }}
      />

      <AddColumnModal
        visible={addColumnOpen || !!editColumn}
        initialColumn={editColumn}
        error={columnConfigError}
        saving={columnMutationLoading}
        onDismiss={() => {
          if (columnMutationLoading) return;
          setColumnConfigError("");
          setAddColumnOpen(false);
          setEditColumn(null);
        }}
        onAdd={editColumn ? handleEditColumn : handleAddColumn}
      />

      <Modal
        visible={!!renameDefaultColumn}
        header={`Rename ${renameDefaultColumn?.defaultName || "column"}`}
        onDismiss={() => {
          if (columnMutationLoading) return;
          setColumnConfigError("");
          setRenameDefaultColumn(null);
        }}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                disabled={columnMutationLoading}
                onClick={() => setRenameDefaultColumn(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={columnMutationLoading}
                disabled={!renameDefaultColumn?.name?.trim()}
                onClick={handleRenameDefaultColumn}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {columnConfigError && (
            <Alert type="error" header="Couldn't rename column">
              {columnConfigError}
            </Alert>
          )}
          <Input
            value={renameDefaultColumn?.name || ""}
            onChange={({ detail }) =>
              setRenameDefaultColumn((current) => ({ ...current, name: detail.value }))
            }
          />
        </SpaceBetween>
      </Modal>

      <Modal
        visible={!!deleteColumn}
        header="Delete custom column"
        onDismiss={() => {
          if (!columnMutationLoading) setDeleteColumn(null);
        }}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                disabled={columnMutationLoading}
                onClick={() => setDeleteColumn(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={columnMutationLoading}
                onClick={handleDeleteColumn}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {columnConfigError && (
            <Alert type="error" header="Couldn't delete column">
              {columnConfigError}
            </Alert>
          )}
          <Box>
            Delete the custom column <strong>{deleteColumn?.name}</strong>? This removes the
            column from the saved Task Manager configuration.
          </Box>
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );

  return (
    <AppLayout
      disableContentPaddings={true}
      content={content}
      contentType="default"
      toolsHide={true}
      navigationOpen={navOpen}
      onNavigationChange={() => setNavOpen(!navOpen)}
      navigation={<SideNav activeHref={TASK_MANAGER_ROUTE} />}
    />
  );
};

export default TaskManagerDashboard;
